import moment from 'moment';
import { put, takeLatest } from 'redux-saga/effects';
import { ProjectId } from 'api/models';
import { getProjectFromCollection, projectCollection } from 'collections/project';
import { getProjectTasksFromCollection, taskCollection } from 'collections/tasks';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { updateGanttDatesAction, updatePhaseDatesAction, phaseDependencyCreated } from 'data/projects/actions';
import { formatDate } from 'data/projects/gantt/dates';
import { filterTaskUpdates, getParentStartDate, getPhaseList, getSprintList, getTaskList, normaliseTasks } from 'data/projects/gantt/list-helpers';
import { dependencyCreatedAction, updateSprintSagaAction } from 'data/tasks/actions';
import { getAlignPhaseDatesSetting, getNewDependenciesEnabledSetting, getQueryAdvancedSettingsConfigKey } from 'queries/advanced-settings/advanced-settings';
import { getProjectDependencyMapKey, getProjectSprintsQueryKey } from 'queries/common';
import { EntityType, getDateShiftsForEntity } from 'utils/date-shift';
import { getProjectDetailUrl } from 'utils/urls';
/**
 * Mirrors the legacy `updateTasksDates` slice reducer: pushes the shifted
 * start/due dates onto the task collection (formatted as API date strings).
 */ const applyTaskDateUpdatesToCollection = (updates)=>{
    updates.forEach((update)=>{
        const { id, startDate, dueDate } = update;
        const patch = {};
        if (startDate) {
            patch.startDate = moment(startDate).format(API_DATE_FORMAT);
        }
        if (dueDate) {
            patch.dueDate = moment(dueDate).format(API_DATE_FORMAT);
        }
        if (patch.startDate != null || patch.dueDate != null) {
            // Dependent date shift: op-update the collection only (push, NO API). Only
            // the acted-upon record hits the API; the backend cascades dependents in
            // the DB. `update` here would fire a redundant API call per dependent.
            const existing = taskCollection.get(id);
            if (existing) {
                taskCollection.push([
                    {
                        ...existing,
                        ...patch
                    }
                ]);
            }
        }
    });
};
/**
 * NOTE(state-db): previously mirrored the shifted task dates into the
 * task-list React Query cache with a setQueryData write on the project-tasks
 * query key. That cache no longer exists (the task list is collection-backed
 * and reactive), so the write was a dead no-op. We now push the shifts
 * straight onto the task collection — identical to what
 * `applyTaskDateUpdatesToCollection` does — so the cascade reaches the
 * reactive store for every caller.
 */ export const updateTaskDates = (updates)=>{
    applyTaskDateUpdatesToCollection(updates);
};
export const updateProjectPhases = (projectId, updates, _queryClient)=>{
    const project = getProjectFromCollection(projectId);
    if (project) {
        const { projectPhases: phases } = project;
        const updatedPhases = phases.map((phase)=>{
            const update = updates.find((u)=>u.id === phase.projectPhaseId);
            if (update != null) {
                const { startDate, dueDate } = update;
                const _update = {};
                if (startDate) {
                    _update.startDate = moment(startDate).format(API_DATE_FORMAT);
                }
                if (dueDate) {
                    _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
                }
                return {
                    ...phase,
                    ..._update
                };
            }
            return phase;
        });
        // Detail is collection-backed — push the patched project.
        projectCollection.push([
            {
                ...project,
                projectPhases: updatedPhases
            }
        ]);
    }
};
export const updateProjectSprints = (projectId, updates, queryClient)=>{
    const sprintsData = queryClient.getQueryData(getProjectSprintsQueryKey(ProjectId(projectId)));
    if (sprintsData) {
        const updatedSprints = sprintsData.map((sprint)=>{
            const update = updates.find((u)=>u.id === sprint.sprintId);
            if (update != null) {
                const { startDate, dueDate } = update;
                const _update = {};
                if (startDate) {
                    _update.startDate = moment(startDate).format(API_DATE_FORMAT);
                }
                if (dueDate) {
                    _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
                }
                return {
                    ...sprint,
                    ..._update
                };
            }
            return sprint;
        });
        queryClient.setQueryData(getProjectSprintsQueryKey(ProjectId(projectId)), ()=>updatedSprints);
    }
};
function* onUpdateSprint({ payload }) {
    var _queryClient_getQueryData;
    const { sprintId, projectId, data } = payload;
    const _projectId = ProjectId(projectId);
    if (!window.location.pathname.includes(getProjectDetailUrl(_projectId))) {
        return;
    }
    const queryClient = yield getQueryClientContext();
    const project = getProjectFromCollection(projectId);
    const sprints = (_queryClient_getQueryData = queryClient.getQueryData(getProjectSprintsQueryKey(_projectId))) !== null && _queryClient_getQueryData !== void 0 ? _queryClient_getQueryData : [];
    const dependenciesMap = queryClient.getQueryData(getProjectDependencyMapKey(projectId));
    // NOTE(state-db): task list is collection-backed; read it imperatively.
    const queryTasks = getProjectTasksFromCollection(projectId);
    // const ganttRef = (yield getContext('ganttRef')) as any;
    if (!project || !queryTasks || !dependenciesMap) {
        return;
    }
    const { startDate, dueDate } = data;
    /**
   * dates will be set as null on clearing dates
   * hence processing null values.
   */ if (!(startDate || startDate === null) && !(dueDate || dueDate === null)) {
        return;
    }
    const { projectPhases } = project;
    const tasks = normaliseTasks(queryTasks);
    const phaseList = getPhaseList(projectPhases);
    const taskList = getTaskList(tasks, phaseList);
    const sprintList = getSprintList(sprints, phaseList);
    const currentEntity = sprintList[sprintId];
    const taskRecord = sprints.find((_sprint)=>_sprint.sprintId === sprintId);
    const updatedSprint = {
        id: sprintId,
        type: EntityType.Sprint,
        depth: currentEntity.depth
    };
    if (startDate) {
        updatedSprint.startDate = formatDate(startDate);
        if (!(taskRecord === null || taskRecord === void 0 ? void 0 : taskRecord.dueDate) && !dueDate) {
            updatedSprint.dueDate = formatDate(startDate);
        }
    }
    if (dueDate) {
        updatedSprint.dueDate = formatDate(dueDate);
        if (!(taskRecord === null || taskRecord === void 0 ? void 0 : taskRecord.startDate) && !startDate) {
            updatedSprint.startDate = formatDate(dueDate);
        }
    }
    /** Handle clearing dates */ if (startDate === null && dueDate === null) {
        const parentStartDate = formatDate(getParentStartDate(sprintId, {
            ...phaseList,
            ...sprintList
        }));
        updatedSprint.startDate = parentStartDate;
        updatedSprint.dueDate = parentStartDate;
    } else if (startDate === null) {
        updatedSprint.startDate = formatDate(dueDate || sprintList[sprintId].dueDate);
    } else if (dueDate === null) {
        updatedSprint.dueDate = formatDate(startDate || sprintList[sprintId].startDate);
    }
    const accountSettings = queryClient.getQueryData(getQueryAdvancedSettingsConfigKey());
    const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
    const newDependenciesSettingEnabled = getNewDependenciesEnabledSetting(accountSettings);
    const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(phaseList, taskList, sprintList, updatedSprint, {
        alignPhaseWithTask: alignPhaseDates,
        enabledNewDependencies: newDependenciesSettingEnabled
    });
    yield put(updateGanttDatesAction({
        phaseUpdates,
        taskUpdates,
        phaseList,
        taskList,
        projectPhases,
        sprintUpdates,
        sprintList
    }));
    updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
    updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
    updateTaskDates(Object.values(taskUpdates));
}
function* onUpdatePhase(action) {
    const { data } = action.payload;
    const { projectId, phaseId, startDate, dueDate } = data;
    const _projectId = ProjectId(projectId);
    if (!window.location.pathname.includes(getProjectDetailUrl(_projectId))) {
        return;
    }
    const queryClient = yield getQueryClientContext();
    const project = getProjectFromCollection(projectId);
    const queryTasks = getProjectTasksFromCollection(projectId);
    const sprints = queryClient.getQueryData(getProjectSprintsQueryKey(_projectId));
    const dependenciesMap = queryClient.getQueryData(getProjectDependencyMapKey(projectId));
    if (!project || !queryTasks || !dependenciesMap) {
        return;
    }
    const { projectPhases } = project;
    const tasks = normaliseTasks(queryTasks);
    const phaseList = getPhaseList(projectPhases);
    const taskList = getTaskList(tasks, phaseList);
    const sprintList = getSprintList(sprints !== null && sprints !== void 0 ? sprints : [], phaseList);
    const currentEntity = phaseList[phaseId];
    const updatedPhase = {
        id: phaseId,
        type: EntityType.Phase,
        depth: currentEntity.depth
    };
    const phaseRecord = projectPhases.find((_phase)=>_phase.projectPhaseId === phaseId);
    if (startDate) {
        updatedPhase.startDate = formatDate(startDate);
        if (!(phaseRecord === null || phaseRecord === void 0 ? void 0 : phaseRecord.dueDate) && !dueDate) {
            updatedPhase.dueDate = formatDate(startDate);
        }
    }
    if (dueDate) {
        updatedPhase.dueDate = formatDate(dueDate);
        if (!(phaseRecord === null || phaseRecord === void 0 ? void 0 : phaseRecord.startDate) && !startDate) {
            updatedPhase.startDate = formatDate(dueDate);
        }
    }
    const accountSettings = queryClient.getQueryData(getQueryAdvancedSettingsConfigKey());
    const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
    const newDependenciesSettingEnabled = getNewDependenciesEnabledSetting(accountSettings);
    const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(phaseList, taskList, sprintList, updatedPhase, {
        alignPhaseWithTask: alignPhaseDates,
        enabledNewDependencies: newDependenciesSettingEnabled
    });
    yield put(updateGanttDatesAction({
        phaseUpdates,
        taskUpdates,
        phaseList,
        taskList,
        projectPhases,
        sprintUpdates,
        sprintList
    }));
    // if (startDate || dueDate) {
    //   const scrollToDate = startDate ? new Date(startDate) : new Date(dueDate);
    //   ganttRef?.instance.scrollToDate(scrollToDate);
    // }
    updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
    updateTaskDates(Object.values(taskUpdates));
    updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
}
function* onDependencyCreated({ payload }) {
    var _taskList_dependentTaskId, _taskList_taskId;
    const { taskId, projectId, dependentTaskId } = payload;
    const queryClient = yield getQueryClientContext();
    const queryTasks = getProjectTasksFromCollection(projectId);
    const project = getProjectFromCollection(projectId);
    const sprints = queryClient.getQueryData(getProjectSprintsQueryKey(ProjectId(projectId)));
    const dependenciesMap = queryClient.getQueryData(getProjectDependencyMapKey(projectId));
    if (!project || !dependenciesMap) {
        return;
    }
    const { projectPhases } = project;
    const tasks = normaliseTasks(queryTasks);
    const phaseList = getPhaseList(projectPhases);
    const taskList = getTaskList(tasks, phaseList);
    const sprintList = getSprintList(sprints !== null && sprints !== void 0 ? sprints : [], phaseList);
    const accountSettings = queryClient.getQueryData(getQueryAdvancedSettingsConfigKey());
    const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
    const newDependenciesSettingEnabled = getNewDependenciesEnabledSetting(accountSettings);
    const entityPayload = dependentTaskId ? {
        id: dependentTaskId,
        dependencyFor: [
            ...(taskList === null || taskList === void 0 ? void 0 : (_taskList_dependentTaskId = taskList[dependentTaskId]) === null || _taskList_dependentTaskId === void 0 ? void 0 : _taskList_dependentTaskId.dependencyFor) || [],
            {
                id: taskId,
                dependencyType: 2,
                lag: null
            }
        ],
        type: EntityType.Task,
        depth: taskList[dependentTaskId].depth
    } : {
        id: taskId,
        dependentOn: [
            ...(taskList === null || taskList === void 0 ? void 0 : (_taskList_taskId = taskList[taskId]) === null || _taskList_taskId === void 0 ? void 0 : _taskList_taskId.dependentOn) || []
        ],
        type: EntityType.Task,
        depth: taskList[taskId].depth
    };
    const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(phaseList, taskList, sprintList, entityPayload, {
        alignPhaseWithTask: alignPhaseDates,
        dependencyOperationsDone: true,
        enabledNewDependencies: newDependenciesSettingEnabled
    });
    yield put(updateGanttDatesAction({
        phaseUpdates,
        taskUpdates,
        phaseList,
        taskList,
        projectPhases,
        sprintUpdates,
        sprintList
    }));
    applyTaskDateUpdatesToCollection(Object.values(filterTaskUpdates(taskUpdates, tasks)));
    updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
    updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
    updateTaskDates(Object.values(taskUpdates));
}
function* onPhaseDependencyCreated({ payload }) {
    var _phaseList_projectPhaseId;
    const { projectPhaseId, predecessorDependencies, projectId } = payload;
    const queryClient = yield getQueryClientContext();
    const project = getProjectFromCollection(projectId);
    const sprints = queryClient.getQueryData(getProjectSprintsQueryKey(ProjectId(projectId)));
    const queryTasks = getProjectTasksFromCollection(projectId);
    const tasks = normaliseTasks(queryTasks);
    const dependenciesMap = queryClient.getQueryData(getProjectDependencyMapKey(projectId));
    if (!project || !dependenciesMap) {
        return;
    }
    const { projectPhases } = project;
    const phaseList = getPhaseList(projectPhases);
    const taskList = getTaskList(tasks, phaseList);
    const sprintList = getSprintList(sprints !== null && sprints !== void 0 ? sprints : [], phaseList);
    const accountSettings = queryClient.getQueryData(getQueryAdvancedSettingsConfigKey());
    const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);
    const newDependenciesSettingEnabled = getNewDependenciesEnabledSetting(accountSettings);
    const [phaseUpdates, taskUpdates, sprintUpdates] = getDateShiftsForEntity(phaseList, taskList, sprintList, {
        id: projectPhaseId,
        dependentOn: [
            ...(phaseList === null || phaseList === void 0 ? void 0 : (_phaseList_projectPhaseId = phaseList[projectPhaseId]) === null || _phaseList_projectPhaseId === void 0 ? void 0 : _phaseList_projectPhaseId.dependentOn) || [],
            ...predecessorDependencies || []
        ],
        type: EntityType.Phase,
        depth: phaseList[projectPhaseId].depth
    }, {
        alignPhaseWithTask: alignPhaseDates,
        dependencyOperationsDone: true,
        enabledNewDependencies: newDependenciesSettingEnabled
    });
    yield put(updateGanttDatesAction({
        phaseUpdates,
        taskUpdates,
        phaseList,
        taskList,
        projectPhases,
        sprintUpdates,
        sprintList
    }));
    applyTaskDateUpdatesToCollection(Object.values(filterTaskUpdates(taskUpdates, tasks)));
    updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
    updateProjectSprints(projectId, Object.values(sprintUpdates), queryClient);
    updateTaskDates(Object.values(taskUpdates));
}
export const watchDateShiftSaga = function*() {
    // NOTE(state-db): task-row gantt sync moved to useGanttTaskCollectionSync.
    yield takeLatest(updateSprintSagaAction.type, onUpdateSprint);
    yield takeLatest(dependencyCreatedAction.type, onDependencyCreated);
    yield takeLatest(updatePhaseDatesAction.type, onUpdatePhase);
    yield takeLatest(phaseDependencyCreated.type, onPhaseDependencyCreated);
};
